// Search-engine title and description for every static route. Applied in the app
// by RouteSeo (App.js, via useRouteSeo in utils/seo.js) and written into a static
// HTML file per route at build time (scripts/prerender-meta.mjs), so crawlers and
// link previews see each page's own title, description and canonical URL before
// any JavaScript runs. Guide pages come from data/guides.js instead.
//
// Keep this file free of imports: the build script loads it directly in Node.
//
//   title, description   what search results and link previews show
//   canonical            the URL to index instead, for an alias route
//   noindex              keep the page out of search results

const SITE_NAME = 'LE Tech Tools';
const t = name => `${name} | ${SITE_NAME}`;

export const SEO_PAGES = {
  '/': {
    title: 'LE Tech Tools — Private, Browser-Based Tools for Law Enforcement',
    description: 'Free, 100% browser-based tools for investigators: LPR pattern analysis, Facebook, Instagram, Snapchat, Apple & Google warrant-return parsers, DVR time & UTC converters, EXIF viewer, and more. Nothing leaves your browser.',
  },
  '/tools': {
    title: 'All Tools — LE Tech Tools',
    description: 'Browse every LE Tech Tool: LPR Pattern Analyzer, REKOR/ALPR Tracker, Facebook, Instagram, Snapchat, Google & Apple warrant-return parsers, DVR time & UTC converters, EXIF viewer, preservation and subpoena generators, and the live Video Canvass Tracker. 100% private.',
  },
  '/guides': {
    title: 'Tool Guides — How Every LE Tech Tool Works | LE Tech Tools',
    description: 'Short guides explaining how each LE Tech Tool works: LPR pattern analysis, legal-return parsers, Map Search, time converters, EXIF viewer, phone-record analysis, and field operations. Free and privacy-focused.',
  },
  '/code-validation': {
    title: 'Is it private? Client-Side Code Validation | LE Tech Tools',
    description: 'How we keep LE Tech Tools private: every tool runs 100% in your browser and no uploaded file, plate, image, or case data is ever sent to a server. Independently reviewed and the exceptions fully disclosed.',
  },
  '/privacy': {
    title: 'Privacy Policy | LE Tech Tools',
    description: 'LE Tech Tools privacy policy: what data we do and do not collect. Case files, uploads, and documents are processed entirely in your browser and never sent to a server, with disclosed exceptions for collaborative and online lookup tools.',
  },

  // ── License plates & vehicles
  '/lprpattern': {
    title: t('LPR Pattern Analyzer — License Plate Reader Data Analysis'),
    description: 'Analyze one vehicle\'s license plate reader (LPR/ALPR) reads from OpenALPR, REKOR, Vigilant or NYPD exports: hourly and day-of-week patterns, common routes, and a next-camera prediction. Free; runs in your browser.',
  },
  '/lprtracker': {
    title: t('REKOR / ALPR Tracker — Find Plates Across Multiple LPR Sites'),
    description: 'Find license plates that appear at several LPR camera sites in REKOR / OpenALPR CSV exports, ranked by how many locations each plate hit. Free and private: the data never leaves your browser.',
  },
  '/vindecoder': {
    title: 'VIN Decoder — LE Tech Tools',
    description: 'Validate a VIN\'s check digit, and decode its country of origin, likely manufacturer, and model year — entirely in your browser. Optional full decode (make, model, engine, plant) via NHTSA\'s free public API.',
  },
  '/commonanalysis': {
    title: t('Common Analysis — Find Common Numbers & Plates Across Datasets'),
    description: 'Find the items that appear in more than one dataset, such as the same phone numbers across call records or the same plates across LPR exports. Free; runs entirely in your browser.',
  },
  '/tagtail': {
    title: 'TagTail — Portable, Covert License Plate Reader You Build Yourself | LE Tech Tools',
    description: 'TagTail is a portable, battery-powered license plate reader built from off-the-shelf parts for about $420. Deploys in minutes, runs covertly behind the windshield, and keeps your reads on your own server.',
  },

  // ── Legal process returns
  '/fbscan': {
    title: t('Facebook Warrant Return Parser — Meta Records Viewer'),
    description: 'Open a Facebook (Meta) search warrant return and browse account info, messages, IP addresses and friends in a readable layout. Processed entirely in your browser; nothing is uploaded.',
  },
  '/instagramscan': {
    title: t('Instagram Warrant Return Parser'),
    description: 'Open an Instagram search warrant return and browse account info, photos, videos, followers, direct messages and IP addresses. Processed entirely in your browser; nothing is uploaded.',
  },
  '/snapchat': {
    title: t('Snapchat Subpoena Return Parser'),
    description: 'Open a Snapchat law enforcement subpoena return ZIP to view account details, IP history and privacy settings in a readable report. Processed entirely in your browser; nothing is uploaded.',
  },
  '/snapchatwarrant': {
    title: t('Snapchat Warrant Return Parser — Chats, Memories & IPs'),
    description: 'Parse Snapchat search warrant return CSV files to read chats, IP history, friends, memories and more, with an analysis report. Processed entirely in your browser; nothing is uploaded.',
  },
  '/applelegaldownload': {
    title: t('Apple Legal Process Download — Decrypt Apple GPG Returns'),
    description: 'Decrypt Apple\'s GPG-encrypted legal process returns: upload the file from Apple, enter the passphrase, and the tool downloads, decrypts and organizes every file by category, in your browser.',
  },
  '/applelegal': {
    title: t('Apple Legal Process Organizer'),
    description: 'Group and organize Apple legal process returns by account for easy review and download. Runs entirely in your browser.',
  },
  '/apple2703d': {
    title: t('Apple Subpoena & 2703(d) Return Parser — iCloud Records Viewer'),
    description: 'Browse an Apple subpoena or 2703(d) return: subscriber details, iCloud activity, iMessage and FaceTime queries, mail logs, devices, purchases and IP addresses, with an analysis page. Runs in your browser.',
  },

  // ── Phones & internet
  '/cdranalyzer': {
    title: t('Phone Record Processing — AT&T, Verizon & T-Mobile Call Records'),
    description: 'Load AT&T, Verizon and T-Mobile phone record returns: auto-detected, merged across responses and de-duplicated, with subscriber info, top callers and time-zone-aware call detail. Runs in your browser.',
  },
  '/phonelookup': {
    title: t('Commercial Phone Number Lookup — Identify Business Numbers'),
    description: 'Bulk-check phone numbers against about 15 million U.S. business listings built into the site, to spot businesses in call records. 100% local: the numbers are never sent anywhere.',
  },
  '/iplookup': {
    title: t('IP Address Lookup for Investigators — VPN, Tor & Datacenter Check'),
    description: 'Screen one IP address or a whole log for VPN, datacenter, Tor, iCloud Private Relay and abuse-list hits using lists held in your browser, so the addresses are never sent anywhere. Optional live lookup.',
  },
  '/pingmapper': {
    title: 'Ping Mapper — LE Tech Tools',
    description: 'Paste a cell-ping GPS coordinate and radius to see the search area plotted on a map.',
  },

  // ── Requests & paperwork
  '/preservation': {
    title: t('Preservation Letter Generator — Phone Records & Area Searches'),
    description: 'Generate preservation request letters for phone records and area searches, ready to send to the carrier. Free; runs entirely in your browser.',
  },
  '/subpoenarider': {
    title: t('Provider Directory & Subpoena Rider Generator — Who to Serve'),
    description: 'Find who to serve for an email, phone number, URL or username; how each provider takes legal process and emergency requests; and what a subpoena, 2703(d) order or warrant can reach. Build the rider in Word.',
  },
  '/providers': { canonical: '/subpoenarider' },

  // ── Photos & evidence files
  '/exif': {
    title: t('EXIF Viewer — Photo Metadata, GPS & Timestamps'),
    description: 'Read photo metadata (EXIF): GPS coordinates, device make and model, and original timestamps. Supports JPEG, HEIC, PNG and more; the files never leave your browser.',
  },
  '/imageconverter': {
    title: 'HEIC Image Converter — LE Tech Tools',
    description: 'Convert iPhone HEIC photos to JPG or PNG entirely in your browser. Nothing is uploaded.',
  },
  '/hashverifier': {
    title: 'File Hash / Chain-of-Custody Verifier — LE Tech Tools',
    description: 'Compute SHA-256, SHA-1, MD5, and SHA-512 hashes of an evidence file, verify against a known value, and export a hash log for chain-of-custody documentation. Runs entirely in your browser.',
  },

  // ── Time, audio & video
  '/dvrtime': {
    title: t('DVR Time Converter — Correct a DVR / CCTV Clock Offset'),
    description: 'Work out a DVR\'s clock offset and convert real incident time to DVR time when a surveillance system\'s clock is wrong. Free tool for video canvassing; runs in your browser.',
  },
  '/utcconverter': {
    title: t('UTC Converter — UTC to Local Time for Records & Returns'),
    description: 'Convert UTC timestamps from phone records, warrant returns and logs to local time and back. Free; runs entirely in your browser.',
  },
  '/transcribe': {
    title: 'Audio & Video Transcription — LE Tech Tools',
    description: 'Transcribe interviews, calls, and body-cam audio entirely in your browser with Whisper. The file never leaves your computer.',
  },
  '/audioconverter': {
    title: 'Audio Format Converter — LE Tech Tools',
    description: 'Convert audio and video files using an unusual or legacy codec into ordinary MP3 or WAV files that play anywhere, with no special player needed. Runs entirely in your browser.',
  },

  // ── Maps & field work
  '/mapsearch': {
    title: 'Map Search — LE Tech Tools',
    description: 'Find nearby OpenStreetMap features for investigative leads.',
  },
  '/canvass': {
    title: t('Video Canvass Tracker — Shared Map for Surveillance Video Canvassing'),
    description: 'A shared live map for a team pulling surveillance video: log each address, mark whether footage was obtained and whether the subject was seen, and share the link so everyone tracks progress.',
  },
  '/areasearch': {
    title: t('Area Search Coordinator — Live Team Search Map'),
    description: 'A shared live map for a team searching a neighborhood: each officer\'s route draws in real time so everyone sees what has been covered, with pins and notes to avoid going over the same ground.',
  },
  '/timeline': {
    title: t('Case Timeline Builder — Chronological Case Timeline & Map'),
    description: 'Assemble phone records, screenshots, interviews and reports into one chronological timeline with a map of where each event happened, exported as a PDF. Saved locally on your device.',
  },

  // ── Not for search results
  '/admin': { title: 'Admin | LE Tech Tools', noindex: true },
  '/testPage': { title: 'Test | LE Tech Tools', noindex: true },
};

// Shared-link pages (an unguessable id after the tool's path) are never indexed.
export const NOINDEX_PREFIXES = ['/canvass/', '/areasearch/', '/timeline/'];
